import React, { useEffect, useMemo, useState } from "react";
import { AccountBalanceWallet, Add, Delete, Edit, Save } from "@mui/icons-material";
import { createFund, createInvestor, deleteInvestor, fetchAccounts, fetchFunds, fetchInvestors, updateInvestor } from "../services/owdedApi";
import { useAuth } from "../context/AuthContext";
import { money } from "./format";
import { EmptyState, LoadingOverlay, Modal, Pagination, StatusPill } from "./ui";
import { paginateRows, PAGE_SIZE } from "./listUtils";
import { hasSuperAccess } from "../utils/access";

const initialInvestor = { name: "", contact_name: "", phone: "", email: "", address: "", status: "ACTIVE" };
const initialFund = { investor_id: "", currency: "CDF", committed_amount: 0 };

function FundsPage() {
  const { user } = useAuth();
  const [investors, setInvestors] = useState([]);
  const [funds, setFunds] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [fundSearch, setFundSearch] = useState("");
  const [investorSearch, setInvestorSearch] = useState("");
  const [fundPage, setFundPage] = useState(1);
  const [investorPage, setInvestorPage] = useState(1);
  const [modal, setModal] = useState(null);
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [investorForm, setInvestorForm] = useState(initialInvestor);
  const [fundForm, setFundForm] = useState(initialFund);
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);

  const canEditFunds = hasSuperAccess(user);

  const load = async () => {
    const [investorRows, fundRows, accountRows] = await Promise.all([fetchInvestors(), fetchFunds(), fetchAccounts().catch(() => [])]);
    setInvestors(investorRows || []);
    setFunds(fundRows || []);
    setAccounts(accountRows || []);
  };

  useEffect(() => { load().catch(() => setNotice("Impossible de charger les fonds.")); }, []);
  useEffect(() => { setFundPage(1); }, [fundSearch, funds.length]);
  useEffect(() => { setInvestorPage(1); }, [investorSearch, investors.length]);

  const filteredFunds = useMemo(() => {
    const term = fundSearch.toLowerCase();
    return funds.filter((fund) => [fund.investor_name, fund.currency, fund.status].filter(Boolean).some((value) => String(value).toLowerCase().includes(term)));
  }, [fundSearch, funds]);
  const filteredInvestors = useMemo(() => {
    const term = investorSearch.toLowerCase();
    return investors.filter((investor) => [investor.name, investor.contact_name, investor.phone, investor.email, investor.address, investor.status].filter(Boolean).some((value) => String(value).toLowerCase().includes(term)));
  }, [investorSearch, investors]);
  const fundPagination = useMemo(() => paginateRows(filteredFunds, fundPage, PAGE_SIZE), [filteredFunds, fundPage]);
  const investorPagination = useMemo(() => paginateRows(filteredInvestors, investorPage, PAGE_SIZE), [filteredInvestors, investorPage]);

  const bankAccountTarget = useMemo(() => {
    const sameCurrency = accounts.filter((account) => account.currency === fundForm.currency && account.status === "ACTIVE");
    return sameCurrency.find((account) => account.account_type === "BANK") || sameCurrency.find((account) => String(account.name || "").toLowerCase().includes("bancaire")) || null;
  }, [accounts, fundForm.currency]);

  const totals = useMemo(() => funds.reduce((acc, fund) => {
    const code = fund.currency || "CDF";
    if (!acc[code]) acc[code] = { available: 0, invested: 0, pending: 0 };
    acc[code].available += Number(fund.available_amount || 0);
    acc[code].invested += Number(fund.invested_amount || 0);
    acc[code].pending += Number(fund.pending_amount || 0);
    return acc;
  }, {}), [funds]);

  const openInvestor = (investor = null) => {
    setEditingInvestor(investor);
    setInvestorForm(investor ? {
      name: investor.name || "",
      contact_name: investor.contact_name || "",
      phone: investor.phone || "",
      email: investor.email || "",
      address: investor.address || "",
      status: investor.status || "ACTIVE",
    } : initialInvestor);
    setModal("investor");
  };

  const submitInvestor = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editingInvestor) await updateInvestor(editingInvestor.id, investorForm);
      else await createInvestor(investorForm);
      setInvestorForm(initialInvestor);
      setEditingInvestor(null);
      setModal(null);
      setNotice(editingInvestor ? "Investisseur modifie." : "Investisseur cree.");
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Impossible d enregistrer l investisseur.");
    } finally {
      setSaving(false);
    }
  };

  const removeInvestor = async (investor) => {
    if (!window.confirm(`Retirer l investisseur ${investor.name} ?`)) return;
    setSaving(true);
    try {
      await deleteInvestor(investor.id);
      setNotice("Investisseur retire.");
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Impossible de retirer cet investisseur.");
    } finally {
      setSaving(false);
    }
  };

  const submitFund = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await createFund(fundForm);
      setFundForm(initialFund);
      setModal(null);
      setNotice("Fonds cree et compte bancaire de la devise credite.");
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Impossible d enregistrer le fonds.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="page-content management-page">
      <div className="page-head"><div><h1>Fonds d investissement</h1><p>Investisseurs, capitaux disponibles et approvisionnement bancaire par devise.</p></div>{canEditFunds && <button className="primary-btn" type="button" onClick={() => setModal("fund")}><Add /> Ajouter un fonds</button>}</div>
      {notice && <p className="notice">{notice}</p>}

      <section className="grid-2">{["CDF", "USD"].map((code) => <article className="mini-card" key={code}><span>Fonds disponibles {code}</span><strong>{money(totals[code]?.available, code)}</strong><small>Investi {money(totals[code]?.invested, code)} / attente {money(totals[code]?.pending, code)}</small></article>)}</section>
      
          <br/>
      <section className="panel data-panel">
        <div className="table-toolbar"><div><h2 className="section-title">Investisseurs</h2><small>{filteredInvestors.length} investisseur(s)</small></div><input placeholder="Rechercher un investisseur..." value={investorSearch} onChange={(event) => setInvestorSearch(event.target.value)} />{canEditFunds && <button className="ghost-btn" type="button" onClick={() => openInvestor()}><Add /> Investisseur</button>}</div>
        {filteredInvestors.length ? <div className="table-scroll"><table className="data-table"><thead><tr><th>Investisseur</th><th>Contact</th><th>Telephone</th><th>Email</th><th>Adresse</th><th>Statut</th>{canEditFunds && <th>Actions</th>}</tr></thead><tbody>{investorPagination.rows.map((investor) => <tr key={investor.id}><td><strong>{investor.name}</strong></td><td>{investor.contact_name || "-"}</td><td>{investor.phone || "-"}</td><td>{investor.email || "-"}</td><td>{investor.address || "-"}</td><td><StatusPill value={investor.status} /></td>{canEditFunds && <td><div className="table-actions"><button className="icon-action" type="button" onClick={() => openInvestor(investor)} aria-label="Modifier"><Edit /></button><button className="icon-action danger" type="button" onClick={() => removeInvestor(investor)} aria-label="Retirer"><Delete /></button></div></td>}</tr>)}</tbody></table></div> : <EmptyState />}
        <Pagination page={investorPagination.safePage} totalPages={investorPagination.totalPages} totalItems={filteredInvestors.length} from={investorPagination.from} to={investorPagination.to} onPageChange={setInvestorPage} />
      </section>
          <br/>

      <section className="panel data-panel">
        <div className="table-toolbar"><div><h2 className="section-title">Fonds enregistres</h2><small>{filteredFunds.length} fonds</small></div><input placeholder="Recherche avancee fonds..." value={fundSearch} onChange={(event) => setFundSearch(event.target.value)} /></div>
        {filteredFunds.length ? <div className="table-scroll"><table className="data-table"><thead><tr><th>Investisseur</th><th>Disponible</th><th>Investi</th><th>Attente</th><th>Recupere</th><th>Retard</th><th>Perdu</th><th>Statut</th></tr></thead><tbody>{fundPagination.rows.map((fund) => <tr key={fund.id}><td>{fund.investor_name}<br /><small>{fund.currency}</small></td><td>{money(fund.available_amount, fund.currency)}</td><td>{money(fund.invested_amount, fund.currency)}</td><td>{money(fund.pending_amount, fund.currency)}</td><td>{money(fund.recovered_amount, fund.currency)}</td><td>{money(fund.late_amount, fund.currency)}</td><td>{money(fund.lost_amount, fund.currency)}</td><td><StatusPill value={fund.status} /></td></tr>)}</tbody></table></div> : <EmptyState />}
        <Pagination page={fundPagination.safePage} totalPages={fundPagination.totalPages} totalItems={filteredFunds.length} from={fundPagination.from} to={fundPagination.to} onPageChange={setFundPage} />
      </section>

      {modal === "investor" && <Modal title={editingInvestor ? "Modifier l investisseur" : "Ajouter un investisseur"} onClose={() => { setModal(null); setEditingInvestor(null); }} footer={<button className="primary-btn" form="investor-form" disabled={saving}><Save /> {saving ? "Enregistrement..." : "Enregistrer"}</button>}><form id="investor-form" onSubmit={submitInvestor}><div className="form-grid"><label className="full-row">Nom de l investisseur<input value={investorForm.name} onChange={(event) => setInvestorForm({ ...investorForm, name: event.target.value })} required /></label><label>Personne de contact<input value={investorForm.contact_name} onChange={(event) => setInvestorForm({ ...investorForm, contact_name: event.target.value })} /></label><label>Telephone<input value={investorForm.phone} onChange={(event) => setInvestorForm({ ...investorForm, phone: event.target.value })} /></label><label>Email<input type="email" value={investorForm.email} onChange={(event) => setInvestorForm({ ...investorForm, email: event.target.value })} /></label><label>Statut<select value={investorForm.status} onChange={(event) => setInvestorForm({ ...investorForm, status: event.target.value })}><option value="ACTIVE">Actif</option><option value="INACTIVE">Inactif</option></select></label><label className="full-row">Adresse<textarea value={investorForm.address} onChange={(event) => setInvestorForm({ ...investorForm, address: event.target.value })} /></label></div></form></Modal>}

      {modal === "fund" && <Modal title="Ajouter un fonds" onClose={() => setModal(null)} footer={<button className="primary-btn" form="fund-form" disabled={saving || !bankAccountTarget}><Save /> {saving ? "Enregistrement..." : "Enregistrer"}</button>}><form id="fund-form" onSubmit={submitFund}><div className="form-grid"><label>Investisseur<select value={fundForm.investor_id} onChange={(event) => setFundForm({ ...fundForm, investor_id: event.target.value })} required><option value="">Selectionner</option>{investors.filter((investor) => investor.status === "ACTIVE").map((investor) => <option value={investor.id} key={investor.id}>{investor.name}</option>)}</select></label><label>Devise<select value={fundForm.currency} onChange={(event) => setFundForm({ ...fundForm, currency: event.target.value })}><option value="CDF">CDF</option><option value="USD">USD</option></select></label><label>Montant engage<input type="number" min="1" value={fundForm.committed_amount} onChange={(event) => setFundForm({ ...fundForm, committed_amount: event.target.value })} required /></label><div className="field-note full-row"><AccountBalanceWallet /> Compte cible : {bankAccountTarget ? `${bankAccountTarget.name} (${bankAccountTarget.currency})` : `Aucun compte bancaire ${fundForm.currency} detecte`}</div></div></form></Modal>}
      <LoadingOverlay show={saving} label="Traitement en cours..." />
    </main>
  );
}

export default FundsPage;
